import { defineStore } from 'pinia';
import { i18n } from '../../plugins/i18n';
import { createErrorMessage } from '@/util/snackBarUtil';
import {
  AccountInfo,
  AuthenticationError,
  AuthStoreState,
  LoginResponse,
  SignupData,
  UserInfo,
  UserLoginType
} from '../types/auth';
import AuthApi from '../../services/auth/AuthApi';
import router from '@/router';
import UsersApi from '@/services/identity/UsersApi';
import { AccountMembershipType, OrganisationRole } from '../types/identity';
import AccountsApi from '@/services/identity/AccountsApi';
import { AccountLicenseTier } from '../types/admin';
import { getDefaultUserFields } from '@/util/tableSettings';
import { systemSettingStore } from './SystemSettingStore';
import {
  clearSwitchedAccount,
  oAuthEnabled,
  setSwitchedAccount
} from '@/services/OidcService';
import { Account } from '@/store/types/identity';
import {
  getAuthenticationErrorMessage,
  setIdPInLocalStorage
} from '@/util/authentication';
import { refreshGraphQLClient } from '@/setup';

export const LOCAL_AUTH_STORE = 'auth';

export const authenticationStore = defineStore('authenticationStore', {
  state: (): AuthStoreState => {
    const localAuthState = localStorage.getItem(LOCAL_AUTH_STORE);
    if (localAuthState) {
      return JSON.parse(localAuthState) as AuthStoreState;
    } else {
      return {
        signupError: '',
        signupData: {
          registrationUrl: window.location.origin
        } as SignupData,
        signupSuccess: false,
        loggedIn: false,
        loggedInUser: {} as UserInfo,
        loggedInAccount: {} as AccountInfo,
        showNotHomeAccountBar: false,
        authenticationError: {
          code: null,
          message: '',
          status: null
        }
      };
    }
  },

  getters: {
    getUserName: (state: AuthStoreState) =>
      state.loggedInUser.firstName + ' ' + state.loggedInUser.lastName,
    getOrganisationName: (state: AuthStoreState) =>
      state.loggedInAccount.organisation.name ?? '',
    customFields: (state: AuthStoreState) =>
      [...(state.loggedInAccount.customFields || [])].sort((a, b) =>
        a.name.localeCompare(b.name)
      ),
    isUnlicensedAccount: (state: AuthStoreState) =>
      state.loggedInAccount.licenseTier === AccountLicenseTier.UNLICENSED,
    isProAccount: (state: AuthStoreState) =>
      state.loggedInAccount.licenseTier === AccountLicenseTier.PRO,
    isFreeAccount: (state: AuthStoreState) =>
      state.loggedInAccount.licenseTier === AccountLicenseTier.FREE,
    isTrialAccount: (state: AuthStoreState) =>
      state.loggedInAccount.licenseTier === AccountLicenseTier.TRIAL,
    licenseTier: (state: AuthStoreState) => state.loggedInAccount.licenseTier,
    licensedFeatures: (state: AuthStoreState) =>
      state.loggedInAccount.licensedFeatures,
    showExpiredBar: (state: AuthStoreState) =>
      state.loggedInAccount.licenseTier === AccountLicenseTier.UNLICENSED &&
      !state.loggedInAccount.internal,
    isAdminAccount: (state: AuthStoreState) =>
      !!state.loggedInUser?.organisation?.roles?.find(
        (role: string) =>
          role === OrganisationRole.SUPPLIER ||
          role === OrganisationRole.BROKER ||
          role === OrganisationRole.RESELLER ||
          role === OrganisationRole.PLATFORM_OPERATOR
      ),
    allowChangeAccount: (state: AuthStoreState) => {
      const roles = state.loggedInUser?.organisation?.roles?.find(
        (role: string) =>
          role === OrganisationRole.SUPPLIER ||
          role === OrganisationRole.BROKER ||
          role === OrganisationRole.RESELLER ||
          role === OrganisationRole.PLATFORM_OPERATOR
      );
      if (!roles) {
        const isCustomer = state.loggedInUser?.organisation?.roles?.includes(
          OrganisationRole.CUSTOMER
        );
        if (
          !!isCustomer &&
          (state.loggedInUser?.accountMemberships?.length || 0) > 1
        ) {
          return true;
        }
      }
      return roles;
    },
    isInternal: (state: AuthStoreState) =>
      state.loggedInAccount?.internal ?? false,
    getCurrentTableSettings: (state: AuthStoreState) =>
      state.loggedInAccount.userFields,
    numberOfAppBars(state: AuthStoreState): number {
      const bars = [this.showExpiredBar, state.showNotHomeAccountBar];
      return bars.filter(Boolean).length;
    }
  },

  actions: {
    async login(
      user: UserLoginType,
      challenge: string,
      redirect?: string
    ): Promise<LoginResponse> {
      this.resetErrors();
      const res = oAuthEnabled()
        ? await this.loginOidc(user, challenge)
        : await this.loginLegacy(user, redirect);
      return res;
    },
    async loginLegacy(
      user: UserLoginType,
      redirect?: string
    ): Promise<LoginResponse> {
      try {
        const response = await AuthApi.login(user);
        await this.refreshLoginInformation();
        await router.push(redirect ?? '/');
        return response;
      } catch (e) {
        if (e instanceof AuthenticationError) {
          this.setError(e);
        }
        throw e;
      }
    },
    async loginOidc(
      user: UserLoginType,
      challenge: string
    ): Promise<LoginResponse> {
      try {
        return await AuthApi.login(user, challenge);
      } catch (e) {
        if (e instanceof AuthenticationError) {
          this.setError(e);
        }
        throw e;
      }
    },
    async loginViaIdP(challenge: string, slug?: string): Promise<void> {
      try {
        const redirectUrl = await AuthApi.loginViaIdP(challenge, slug);
        setIdPInLocalStorage(slug ?? '');
        window.location.replace(redirectUrl ?? '/');
      } catch (e) {
        if (e instanceof AuthenticationError) {
          this.setError(e);
        }
        throw e;
      }
    },
    updateLocalLanguage() {
      if (this.loggedInUser.preferredLanguage === 'de_DE') {
        localStorage.setItem('currentLang', 'de-DE');
        i18n.locale = 'de-DE';
      }
      if (this.loggedInUser.preferredLanguage === 'en_US') {
        localStorage.setItem('currentLang', 'en-GB');
        i18n.locale = 'en-GB';
      }
    },

    async refreshLoggedInAccountInformation() {
      const accountResponse = await AccountsApi.getAccountInformation();
      const account = accountResponse.data as AccountInfo;
      this.loggedInAccount = account;
      if (!this.getCurrentTableSettings) {
        this.setDefaultUserFields();
      }
      // check if logged into home account
      const accountMembership = this.loggedInUser?.accountMemberships?.find(
        (membership: AccountMembershipType) =>
          membership.accountUuid === this.loggedInAccount.uuid
      );
      this.showNotHomeAccountBar = !accountMembership?.isHomeAccount;
    },
    setDefaultUserFields() {
      const defaultFields = getDefaultUserFields();
      this.loggedInAccount.userFields = defaultFields;
    },

    async signup(signupData: SignupData) {
      try {
        await AuthApi.signup(signupData);
        this.signupSuccess = true;
        this.signupError = '';
      } catch (error) {
        this.signupError = createErrorMessage(error);
      }
    },
    resetStore() {
      this.loggedIn = false;
      this.loggedInUser = <UserInfo>{};
      this.loggedInAccount = <AccountInfo>{};
      this.showNotHomeAccountBar = false;
      localStorage.removeItem(LOCAL_AUTH_STORE);
    },
    setError(error: AuthenticationError) {
      this.authenticationError.code = error.code;
      this.authenticationError.status = error.statusCode;
      this.authenticationError.message = getAuthenticationErrorMessage(
        error.code,
        error.data
      );
    },
    resetErrors() {
      this.authenticationError = {
        code: null,
        message: '',
        status: null
      };
    },

    async refreshLoggedInUser() {
      const response = await UsersApi.getUserInfo();
      this.loggedInUser = response.data as UserInfo;
    },

    async switchAccount(account: Account | null) {
      this.resetErrors();
      try {
        account
          ? setSwitchedAccount({
              namespace: account.namespace,
              accountId: account.uuid
            })
          : clearSwitchedAccount();
        await this.refreshLoginInformation();
        router.go(0);
        return true;
      } catch (e) {
        if (e instanceof AuthenticationError) {
          this.authenticationError.message = getAuthenticationErrorMessage(
            e.code,
            e.data
          );
          this.authenticationError.status = e.statusCode;
          this.authenticationError.code = e.code;
        }
        return false;
      }
    },
    async refreshLoginInformation() {
      await refreshGraphQLClient();
      await systemSettingStore().fetchSettings();
      await this.refreshLoggedInUser();
      await this.refreshLoggedInAccountInformation();
      this.loggedIn = true;
      localStorage.setItem(LOCAL_AUTH_STORE, JSON.stringify(this.$state));
    }
  }
});
